import * as React from 'react';
import {
  PageTitle, Card, Section, Pill, Kpi, Btn,
  Table, Th, Td, fmt,
} from '../components/ui-kit';
import { Activity, FileCheck2, ExternalLink, Database } from 'lucide-react';

/**
 * Bench gallery — renders published benchmark artefacts from `bench/proofs/*`.
 *
 * Numbers below come from the benchmark artefacts captured in
 * `bench/proofs/05_load_match_*vu.json` and `07_corpus_split.json`
 * on 2026-05-05 against https://app-xyqar-api.azurewebsites.net.
 *
 * They are intentionally published as a fixed proof set so auditors can inspect
 * a reproducible reference run even when the tenant-scoped live endpoint
 * `/api/v1/auditor/proofs` is empty. Re-run `bench/run_all.sh` to regenerate.
 */

const LOAD = [
  { vu:   50, duration:  120, req:  3376, err:   0, rps: 27.3, p50:  762, p95:    981, p99:  1301, max:   3370, sla: true  },
  { vu:  100, duration:  186, req: 10034, err:   0, rps: 53.8, p50:  753, p95:    966, p99:  1388, max:   4431, sla: true  },
  { vu:  500, duration:  120, req:  4398, err: 279, rps: 30.0, p50: 8927, p95:  28709, p99: 35887, max:  52475, sla: false },
  { vu: 1000, duration:  120, req:  4822, err: 367, rps: 31.6, p50:18887, p95:  60457, p99: 85753, max: 141606, sla: false },
];

const SPLIT = {
  blind_profiles: { total: 1_000_000, real: 11,      synthetic: 999_989 },
  raw_jobs:       { total: 1_000_000, real: 141_403, synthetic: 858_597 },
  with_embedding: 23_570,
  missions_v3: 80, missions_matched: 80, mission_matches: 240,
  audit_chain: 96_075,
};

const CORPUS_GENESIS = '2026-05-05';

export default function Proofs() {
  return (
    <div>
      <PageTitle
        kicker="Audit · Reproducible"
        title="Published proof artefacts"
        right={<Pill tone="warn"><FileCheck2 className="h-3 w-3" /> published benchmark set</Pill>}
      />

      <Section
        title="What this page is"
        hint="Every number below comes from a JSON artefact in bench/proofs/*. This page shows the published reference run, not tenant live telemetry."
      >
        <Card>
          <p className="text-white/80 text-sm leading-relaxed">
            Xyqar publishes machine-readable benchmark artefacts for infrastructure and corpus claims.
            This page renders a reference run executed against
            <span className="font-mono text-white"> app-xyqar-api.azurewebsites.net</span> and stored under
            <span className="font-mono text-white"> bench/proofs/</span>.
            Tenant-specific live proofs are expected to come from the auditor API once that endpoint is populated.
            Independent third parties can replay the bench harness end-to-end and compare the generated JSON files.
          </p>
        </Card>
      </Section>

      <Section title="Corpus state · 2026-05-05" hint="Honest split: real (crawled / curated) vs synthetic (deterministic ETL, 0 LLM)">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-4">
          <Kpi accent="blue"  label="Anonymised fiches" value={fmt(SPLIT.blind_profiles.total)}
               hint={`${fmt(SPLIT.blind_profiles.real)} real · ${fmt(SPLIT.blind_profiles.synthetic)} synth`} />
          <Kpi accent="blue"  label="Job descriptions"  value={fmt(SPLIT.raw_jobs.total)}
               hint={`${fmt(SPLIT.raw_jobs.real)} real (FT/Adzuna/Jooble/Reed) · ${fmt(SPLIT.raw_jobs.synthetic)} synth`} />
          <Kpi accent="green" label="With embedding"    value={fmt(SPLIT.with_embedding)}
               hint="384-d MiniLM, pgvector ivfflat" />
          <Kpi accent="green" label="Audit chain depth" value={fmt(SPLIT.audit_chain)}
               hint="Ed25519 hash-linked" />
        </div>
        <Card>
          <p className="text-[11px] text-white/60 font-mono">
            artefact: <span className="text-white">bench/proofs/07_corpus_split.json</span> · genesis {CORPUS_GENESIS}
          </p>
        </Card>
      </Section>

      <Section
        title="Published load test · POST /api/v1/client/missions/{id}/match"
        hint="Reference benchmark run: top_n=3, P3v3 single instance, 1M corpus, gunicorn 12 workers, asyncpg pool 30/worker"
        right={<Pill tone="warn"><Activity className="h-3 w-3" /> reference run</Pill>}
      >
        <Table>
          <thead>
            <tr>
              <Th>VU</Th><Th>Duration</Th><Th className="text-right">Req</Th><Th className="text-right">Err</Th>
              <Th className="text-right">RPS</Th><Th className="text-right">p50</Th><Th className="text-right">p95</Th>
              <Th className="text-right">p99</Th><Th className="text-right">max</Th><Th>SLA</Th>
            </tr>
          </thead>
          <tbody>
            {LOAD.map((r) => (
              <tr key={r.vu} className="border-t border-white/5 hover:bg-white/5">
                <Td><span className="text-white font-mono text-sm">{r.vu}</span></Td>
                <Td><span className="text-white/60 font-mono text-xs">{r.duration} s</span></Td>
                <Td className="text-right"><span className="text-white font-mono text-xs">{fmt(r.req)}</span></Td>
                <Td className="text-right"><span className={`font-mono text-xs ${r.err === 0 ? 'text-emerald-300' : 'text-rose-300'}`}>{fmt(r.err)}</span></Td>
                <Td className="text-right"><span className="text-white/80 font-mono text-xs">{r.rps}</span></Td>
                <Td className="text-right"><span className="text-white/80 font-mono text-xs">{fmt(r.p50)} ms</span></Td>
                <Td className="text-right"><span className="text-white/80 font-mono text-xs">{fmt(r.p95)} ms</span></Td>
                <Td className="text-right"><span className={`font-mono text-xs font-bold ${r.p99 < 2000 ? 'text-emerald-300' : 'text-rose-300'}`}>{fmt(r.p99)} ms</span></Td>
                <Td className="text-right"><span className="text-white/40 font-mono text-xs">{fmt(r.max)} ms</span></Td>
                <Td>{r.sla ? <Pill tone="live">PASS</Pill> : <Pill tone="alert">saturated</Pill>}</Td>
              </tr>
            ))}
          </tbody>
        </Table>
        <Card className="mt-4">
          <p className="text-[11px] text-white/60 font-mono leading-relaxed">
            Single P3v3 instance ceiling: ~54 RPS sustained @ 100 VU (CPU-bound on Wolf Brain).
            Beyond 100 VU the queue saturates — fix is horizontal scale-out (currently blocked by
            Azure subscription quota request) plus pgbouncer transaction-pooling. Both are
            mitigated for design-partner pilots in [CLIENT_PILOT_KIT.md](#) by capping concurrency
            and pre-booking match windows.
          </p>
          <p className="text-[11px] text-white/40 font-mono mt-3">
            artefact: <span className="text-white">bench/proofs/05_README.md</span> +{' '}
            <span className="text-white">05_load_match_{`{50,100,500,1000}`}vu.json</span>
          </p>
        </Card>
      </Section>

      <Section title="Sample anonymised fiche" hint="Wolf Brain v3 — 8 dimensions — no PII, only the wolf_id handle">
        <Card>
          <div className="flex items-center justify-between gap-4">
            <div>
              <p className="text-white text-sm">A printable, shareable, tamper-evident HTML fiche per candidate.</p>
              <p className="text-[11px] text-white/40 font-mono mt-1">
                bench/proofs/07_fiche_WOLF-BDF1EFF45309.html · 7.6 KB · no PII
              </p>
            </div>
            <Btn as="a" href="https://app-xyqar-api.azurewebsites.net/api/v1/client/blind/WOLF-BDF1EFF45309/fiche.html">
              <ExternalLink className="h-3 w-3" /> Open sample
            </Btn>
          </div>
        </Card>
      </Section>

      <Section title="How to reproduce" hint="Anyone with the benchmark credentials can replay this published proof set">
        <Card>
          <ol className="list-decimal list-inside text-sm text-white/80 space-y-2 leading-relaxed">
            <li>Clone the repo and check out the commit referenced in the proof file's footer.</li>
            <li>Export <span className="font-mono text-white">XYQAR_BENCH_BYPASS_TOKEN</span> (rate-limit bypass for the bench harness).</li>
            <li>Run <span className="font-mono text-white">bash bench/run_all.sh</span>. The script regenerates every <span className="font-mono">bench/proofs/*</span>.</li>
            <li>Diff the new artefacts against the ones rendered above. They must match (timing fluctuation aside).</li>
            <li>For latency: <span className="font-mono text-white">python bench/locust_match.py --vu 100 --duration 180</span> writes <span className="font-mono">05_load_match_100vu.json</span>.</li>
          </ol>
        </Card>
      </Section>

      <Section title="What is intentionally NOT claimed" hint="Honest limits of this published benchmark set">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <Card><Database className="h-5 w-5 text-amber-400 mb-3" />
            <p className="text-[10px] uppercase tracking-[0.2em] text-white/40 font-mono">1M experts</p>
            <p className="text-white text-sm mt-1">{fmt(SPLIT.blind_profiles.total)} fiches in DB but only{' '}
              <span className="text-amber-300">{fmt(SPLIT.blind_profiles.real)}</span> are curated real seeds.
              The rest are deterministic synth (NIST NICE 200 skills, 12 zones, 0 LLM).
            </p>
          </Card>
          <Card><Database className="h-5 w-5 text-emerald-400 mb-3" />
            <p className="text-[10px] uppercase tracking-[0.2em] text-white/40 font-mono">141k jobs</p>
            <p className="text-white text-sm mt-1">{fmt(SPLIT.raw_jobs.real)} real job descriptions crawled from
              France Travail / Adzuna / Jooble / Reed. The remaining {fmt(SPLIT.raw_jobs.synthetic)} are synth.
            </p>
          </Card>
          <Card><Activity className="h-5 w-5 text-rose-400 mb-3" />
            <p className="text-[10px] uppercase tracking-[0.2em] text-white/40 font-mono">1000 VU SLA</p>
            <p className="text-white text-sm mt-1">SLA p99 &lt; 2 s holds at 100 VU. Beyond, the single P3v3
              instance saturates; horizontal scale-out is blocked by Azure subscription quota.
            </p>
          </Card>
        </div>
      </Section>
    </div>
  );
}
